import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Pencil, Trash2, Check, Copy, Mail, Search, Archive, Plus, X as XIcon, Camera, Images, Maximize2, Clock, Package, QrCode, Star, ScanLine } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useInventory } from '../store/inventory';
import { useGroups } from '../store/groups';
import { useOrganizations } from '../store/organizations';
import { Avatar, BottomSheet, PrimaryBtn, SecondaryBtn, Badge, Field, Input, Select, Textarea, SectionLabel, Divider, Spinner, ErrorMsg, useConfirm, QuantityStepper, ExpandSection, TagInput, CameraCapture, UnitPicker, ImageLightbox, ImageTile, ImageTileBadge } from '../components/UI';
import { sanitizeText } from '../lib/validation';
import { CategoryIcon, CategoryIconPicker } from '../lib/categoryIcons';
import { EditItemSheet } from '../components/EditItemSheet';
import { QRCodeSheet } from '../components/QRCodeSheet';
import { CodeScanner } from '../components/CodeScanner';

const ENHETER_FORSLAG = ['stk', 'kg', 'g', 'liter', 'dl', 'ml', 'meter', 'cm', 'mm', 'm²', 'm³', 'rull', 'pakke', 'brett', 'boks', 'tube', 'sekk', 'kasse', 'sett', 'par'];

const emptyForm = () => ({ navn: '', antall: '1', enhet: 'stk', beskrivelse: '', bilde_url: '', emoji: '' });

function highlightMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'var(--green-light)', color: 'var(--green-dark)', borderRadius: 3, padding: '0 1px' }}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export const InventoryPage: React.FC<{
  addOpen: boolean;
  onCloseAdd: () => void;
  shareOpen: boolean;
  onCloseShare: () => void;
  categoryOpen: boolean;
  onCloseCategory: () => void;
  manageListsOpen?: boolean;
  onCloseManageLists?: () => void;
  tagAdminOpen?: boolean;
  onCloseTagAdmin?: () => void;
  addListOpen?: boolean;
  onCloseAddList?: () => void;
  codeSearchOpen?: boolean;
  onCloseCodeSearch?: () => void;
  quickAddOpen?: boolean;
  onCloseQuickAdd?: () => void;
  searchOpen?: boolean;
}> = ({ addOpen, onCloseAdd, shareOpen, onCloseShare, categoryOpen, onCloseCategory, manageListsOpen: externalManageListsOpen, onCloseManageLists, tagAdminOpen: externalTagAdminOpen, onCloseTagAdmin, addListOpen: externalAddListOpen, onCloseAddList, codeSearchOpen: externalCodeSearchOpen, onCloseCodeSearch, quickAddOpen: externalQuickAddOpen, onCloseQuickAdd, searchOpen }) => {
  const { user, profile, updateProfile } = useAuth();
  const { organizations, myAdminOrgIds, setItemOrganization, orgGroups, fetchOrgGroups, groupOrgInfo } = useOrganizations();
  const {
    items, lists, loading, fetchItems, addItem, deleteItem, uploadImage,
    fetchLists, createList, renameList, deleteList, assignToList, toggleListFavorite,
    sharedWith, sharedInventories,
    fetchShareData, generateShareToken, joinByToken, revokeShare, leaveShare, sendShareEmail,
    addInventoryToGroup, fetchLoanableItems, addItemFilterCategory,
    renamePersonalTag, deletePersonalTag,
    savedTags, fetchSavedTags, addSavedTags,
    itemCodes, itemCodesLoaded, fetchItemCodes, setListCode, resolveItemCode,
  } = useInventory();
  const { groups, currentGroup, currentGroupId, members, tools: groupTools, categories, addCategory, updateCategory, updateToolsCategory, deleteCategory } = useGroups();
  const { confirm, dialog } = useConfirm();

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [aktivKategori, setAktivKategori] = useState<string | null>(null);
  const [aktivListeId, setAktivListeId] = useState<string | null>(null);
  const [loanableFilter, setLoanableFilter] = useState<'all' | 'loanable' | 'not'>('all');
  const [search, setSearch] = useState('');
  const [expandedShares, setExpandedShares] = useState<Set<string>>(new Set());
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListItems, setNewListItems] = useState<string[]>([]);
  const [newListSaving, setNewListSaving] = useState(false);
  const [newListError, setNewListError] = useState('');
  const [newListEmoji, setNewListEmoji] = useState('');
  const [manageListsOpen, setManageListsOpen] = useState(false);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editingListName, setEditingListName] = useState('');
  const [editingListEmoji, setEditingListEmoji] = useState('');
  const [editingListCode, setEditingListCode] = useState('');
  const [editingListCodeError, setEditingListCodeError] = useState('');
  const [qrTarget, setQrTarget] = useState<{ code: string; label: string } | null>(null);
  const [codeSearchOpen, setCodeSearchOpen] = useState(false);
  const [codeSearchValue, setCodeSearchValue] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [codeSearchError, setCodeSearchError] = useState('');
  const [showListDropdown, setShowListDropdown] = useState(false);
  const [listDropPos, setListDropPos] = useState({ top: 0, left: 0 });
  const listBtnRef = useRef<HTMLDivElement>(null);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagDropPos, setTagDropPos] = useState({ top: 0, left: 0 });
  const tagBtnRef = useRef<HTMLDivElement>(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddListId, setQuickAddListId] = useState<string | null>(null);
  const [quickAddRows, setQuickAddRows] = useState<{ navn: string; antall: string; enhet: string }[]>([{ navn: '', antall: '1', enhet: 'stk' }]);
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [quickAddError, setQuickAddError] = useState('');
  const [deleteListSheet, setDeleteListSheet] = useState<{ id: string; name: string; count: number } | null>(null);
  const [deleteMoveTarget, setDeleteMoveTarget] = useState<string | null>(null);
  const [formListId, setFormListId] = useState<string | null>(null);

  const [form, setForm] = useState(emptyForm());
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const [generatingTokenListId, setGeneratingTokenListId] = useState<string | null>(null);
  const [copiedListId, setCopiedListId] = useState<string | null>(null);
  const [activeEmailListId, setActiveEmailListId] = useState<string | null>(null);
  const [joinToken, setJoinToken] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareEmailSending, setShareEmailSending] = useState(false);
  const [shareEmailSent, setShareEmailSent] = useState(false);
  const [shareEmailError, setShareEmailError] = useState('');
  const [customEnhet, setCustomEnhet] = useState(false);
  const [formTags, setFormTags] = useState<string[]>([]);
  const [formLoanable, setFormLoanable] = useState(false);

  const [tagAdminOpen, setTagAdminOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [deletingTag, setDeletingTag] = useState<string | null>(null);
  const [replacementTag, setReplacementTag] = useState('');
  const [tagAdminSaving, setTagAdminSaving] = useState(false);
  const [formGroupIds, setFormGroupIds] = useState<string[]>([]);
  const [formOrganizationId, setFormOrganizationId] = useState('');
  const [formFilterCategoryIds, setFormFilterCategoryIds] = useState<string[]>([]);

  const [showCategories, setShowCategories] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatEmoji, setNewCatEmoji] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [showAddCat, setShowAddCat] = useState(false);
  const [editCatId, setEditCatId] = useState<string | null>(null);
  const [deleteCatId, setDeleteCatId] = useState<string | null>(null);
  const [deleteCatToolCount, setDeleteCatToolCount] = useState(0);
  const [moveToCatId, setMoveToCatId] = useState('');
  const [editCatName, setEditCatName] = useState('');
  const [editCatEmoji, setEditCatEmoji] = useState('');
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    if (user) {
      fetchItems(user.id);
      fetchShareData(user.id);
      fetchLists(user.id);
      fetchSavedTags(user.id);
      fetchItemCodes(user.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (addOpen) setFormListId(aktivListeId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOpen]);

  useEffect(() => {
    if (externalManageListsOpen) { setManageListsOpen(true); setEditingListId(null); }
  }, [externalManageListsOpen]);

  useEffect(() => {
    if (externalTagAdminOpen) { setTagAdminOpen(true); setEditingTag(null); setDeletingTag(null); }
  }, [externalTagAdminOpen]);

  useEffect(() => {
    if (externalAddListOpen) setNewListOpen(true);
  }, [externalAddListOpen]);

  useEffect(() => {
    if (externalCodeSearchOpen) { setCodeSearchOpen(true); setCodeSearchValue(''); setCodeSearchError(''); }
  }, [externalCodeSearchOpen]);

  useEffect(() => {
    if (externalQuickAddOpen) openQuickAdd(aktivListeId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalQuickAddOpen]);

  useEffect(() => {
    if (!searchOpen) setSearch('');
  }, [searchOpen]);

  useEffect(() => {
    if (aktivKategori && !items.some(i => (i.personal_tags ?? []).includes(aktivKategori))) {
      setAktivKategori(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Slår opp en kode og går til riktig liste eller ting. Brukes av «Slå opp kode»,
  // skanning, og ?code=-lenker (via sessionStorage) — samme oppførsel overalt.
  const openResolvedCode = (raw: string): boolean => {
    const result = resolveItemCode(raw);
    if (!result) { setCodeSearchError('Fant ingen ting med denne koden'); setCodeSearchOpen(true); return false; }
    if (result.type === 'list') setAktivListeId(result.id);
    else setDetailId(result.id);
    return true;
  };

  useEffect(() => {
    if (!user || !itemCodesLoaded) return;
    const pending = sessionStorage.getItem('pendingItemCode');
    if (!pending) return;
    sessionStorage.removeItem('pendingItemCode');
    openResolvedCode(pending);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, itemCodesLoaded]);

  // Skanning fra topbaren (App.tsx) når Inventar allerede er åpen: App sender
  // koden i sessionStorage og dispatcher denne hendelsen, så vi slår den opp her.
  useEffect(() => {
    const onPending = () => {
      const pending = sessionStorage.getItem('pendingItemCode');
      if (!pending) return;
      sessionStorage.removeItem('pendingItemCode');
      openResolvedCode(pending);
    };
    window.addEventListener('toolshare:pending-code', onPending);
    return () => window.removeEventListener('toolshare:pending-code', onPending);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f); setImagePreview(URL.createObjectURL(f));
  };

  const handleAdd = async () => {
    if (!form.navn.trim()) { setError('Navn er påkrevd'); return; }
    const antall = form.antall.trim() === '' ? 0 : parseInt(form.antall);
    if (isNaN(antall) || antall < 0) { setError('Antall må være et tall (0 eller mer)'); return; }
    if (formLoanable && formGroupIds.length === 0) { setError('Velg minst én gruppe tingen skal vises i'); return; }
    setSaving(true); setError('');
    try {
      let bilde_url = '';
      if (imageFile) bilde_url = (await uploadImage(imageFile)) || '';
      const newItem = await addItem({
        user_id: user!.id,
        navn: sanitizeText(form.navn, 100),
        kategori: formTags[0] ?? '',
        personal_tags: formTags,
        antall,
        enhet: form.enhet,
        beskrivelse: sanitizeText(form.beskrivelse, 300),
        bilde_url,
        emoji: form.emoji,
        list_id: formListId,
        loanable: formLoanable,
        status: 'available',
        loaned_to_id: null,
        loaned_at: null,
        due_date: null,
        overdue_notified_at: null,
      });
      if (newItem && formLoanable) {
        await Promise.all([
          ...formGroupIds.map(gid => addInventoryToGroup(newItem.id, gid)),
          ...formFilterCategoryIds.map(cid => addItemFilterCategory(newItem.id, cid)),
        ]);
        const curGrpId = currentGroup()?.id;
        if (curGrpId && formGroupIds.includes(curGrpId)) fetchLoanableItems(curGrpId);
      }
      if (newItem && formOrganizationId) await setItemOrganization(newItem.id, formOrganizationId);
      if (formTags.length > 0) await addSavedTags(user!.id, formTags);
      fetchItems(user!.id);
      setForm(emptyForm()); setImageFile(null); setImagePreview(null); setFormListId(null);
      setFormTags([]); setFormLoanable(false); setFormGroupIds([]); setFormFilterCategoryIds([]); setFormOrganizationId('');
      onCloseAdd();
    } catch (e: any) {
      setError(e?.message || 'Noe gikk galt, prøv igjen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!await confirm('Slette denne tingen?', { confirmLabel: 'Slett' })) return;
    await deleteItem(id);
  };

  const handleGenerateToken = async (listId: string) => {
    setGeneratingTokenListId(listId);
    await generateShareToken(listId);
    setGeneratingTokenListId(null);
  };

  const handleCopyToken = async (token: string, listId: string) => {
    try { await navigator.clipboard.writeText(token); } catch {}
    setCopiedListId(listId);
    setTimeout(() => setCopiedListId(null), 2000);
  };

  const handleJoin = async () => {
    if (!joinToken.trim()) { setJoinError('Skriv inn en kode'); return; }
    setJoinLoading(true); setJoinError('');
    const result = await joinByToken(joinToken, user!.id);
    if (!result.ok) { setJoinError(result.message); setJoinLoading(false); return; }
    await fetchShareData(user!.id);
    setJoinToken('');
    setJoinLoading(false);
  };

  const handleRevoke = async (shareId: string, name: string) => {
    if (!await confirm(`Fjerne ${name}s tilgang til din inventarliste?`, { confirmLabel: 'Fjern' })) return;
    await revokeShare(shareId);
  };

  const handleSendEmail = async (listToken: string) => {
    if (!shareEmail.trim()) return;
    setShareEmailSending(true); setShareEmailError(''); setShareEmailSent(false);
    const err = await sendShareEmail(shareEmail.trim(), profile?.full_name || 'En bruker', listToken);
    if (err) { setShareEmailError(err); }
    else { setShareEmailSent(true); setShareEmail(''); setActiveEmailListId(null); setTimeout(() => setShareEmailSent(false), 3000); }
    setShareEmailSending(false);
  };

  const handleLeave = async (shareId: string, listName: string, ownerName: string) => {
    if (!await confirm(`Melde deg av «${listName}» (delt av ${ownerName})?`, { confirmLabel: 'Meld av', danger: false })) return;
    await leaveShare(shareId);
  };

  const handleCreateList = async () => {
    if (!newListName.trim() || !user) return;
    setNewListSaving(true);
    setNewListError('');
    try {
      await createList(newListName.trim(), user.id, newListItems, newListEmoji);
      setNewListName(''); setNewListEmoji(''); setNewListItems([]); setNewListOpen(false); onCloseAddList?.();
    } catch (e: any) {
      setNewListError(e?.message || 'Noe gikk galt. Prøv igjen.');
    } finally {
      setNewListSaving(false);
    }
  };

  const handleDeleteList = (listId: string, listName: string) => {
    const count = items.filter(i => i.list_id === listId).length;
    if (count > 0) {
      setDeleteMoveTarget(null);
      setDeleteListSheet({ id: listId, name: listName, count });
    } else {
      confirm(`Slette listen «${listName}»?`, { confirmLabel: 'Slett liste' }).then(ok => {
        if (!ok) return;
        if (aktivListeId === listId) setAktivListeId(null);
        deleteList(listId);
      });
    }
  };

  const handleConfirmDeleteList = async (mode: 'delete' | 'move') => {
    if (!deleteListSheet) return;
    const { id, name } = deleteListSheet;
    if (mode === 'delete') {
      const ok = await confirm(`Slett alle ${deleteListSheet.count} gjenstander i «${name}» også?`, { confirmLabel: 'Slett alt', danger: true });
      if (!ok) return;
      await Promise.all(items.filter(i => i.list_id === id).map(i => deleteItem(i.id)));
    } else {
      if (deleteMoveTarget) {
        await Promise.all(items.filter(i => i.list_id === id).map(i => assignToList(i.id, deleteMoveTarget)));
      } else {
        await Promise.all(items.filter(i => i.list_id === id).map(i => assignToList(i.id, null)));
      }
    }
    if (aktivListeId === id) setAktivListeId(null);
    await deleteList(id);
    setDeleteListSheet(null);
  };

  const handleCodeSearch = () => {
    const result = resolveItemCode(codeSearchValue);
    if (!result) { setCodeSearchError('Fant ingen ting med denne koden'); return; }
    setCodeSearchOpen(false); onCloseCodeSearch?.();
    if (result.type === 'list') setAktivListeId(result.id);
    else setDetailId(result.id);
  };

  // Skanning inne i «Slå opp kode»: lukker arket og åpner treffet med en gang.
  const handleScannedInSheet = (code: string) => {
    setScannerOpen(false);
    const result = resolveItemCode(code);
    if (!result) { setCodeSearchValue(code); setCodeSearchError('Fant ingen ting med denne koden'); return; }
    setCodeSearchOpen(false); onCloseCodeSearch?.();
    if (result.type === 'list') setAktivListeId(result.id);
    else setDetailId(result.id);
  };

  const toggleFavoriteTag = (tag: string) => {
    const favTags = profile?.favorite_tags ?? [];
    const isFav = favTags.includes(tag);
    updateProfile({ favorite_tags: isFav ? favTags.filter(t => t !== tag) : [...favTags, tag] });
  };

  const openQuickAdd = (listId: string | null) => {
    setQuickAddRows([{ navn: '', antall: '1', enhet: 'stk' }]);
    setQuickAddError('');
    setQuickAddListId(listId);
    setQuickAddOpen(true);
  };

  const closeQuickAdd = () => {
    setQuickAddOpen(false);
    onCloseQuickAdd?.();
  };

  const updateQuickRow = (index: number, field: 'navn' | 'antall' | 'enhet', value: string) => {
    setQuickAddRows(rows => rows.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const addQuickRow = () => {
    setQuickAddRows(rows => [...rows, { navn: '', antall: '1', enhet: 'stk' }]);
  };

  const removeQuickRow = (index: number) => {
    setQuickAddRows(rows => rows.length > 1 ? rows.filter((_, i) => i !== index) : rows);
  };

  const handleQuickAddSave = async () => {
    if (!quickAddListId || !user) { setQuickAddError('Velg en liste først'); return; }
    const validRows = quickAddRows.filter(r => r.navn.trim());
    if (validRows.length === 0) { setQuickAddError('Skriv inn minst ett navn'); return; }
    setQuickAddSaving(true); setQuickAddError('');
    for (const row of validRows) {
      const antallNum = row.antall.trim() === '' ? 1 : parseInt(row.antall);
      await addItem({
        user_id: user.id,
        navn: sanitizeText(row.navn, 100),
        kategori: '',
        personal_tags: [],
        antall: isNaN(antallNum) || antallNum < 0 ? 1 : antallNum,
        enhet: row.enhet.trim() || 'stk',
        beskrivelse: '',
        bilde_url: '',
        emoji: '',
        list_id: quickAddListId,
        loanable: false,
        status: 'available',
        loaned_to_id: null,
        loaned_at: null,
        due_date: null,
        overdue_notified_at: null,
        quick_added: true,
      });
    }
    setQuickAddSaving(false);
    closeQuickAdd();
  };

  const toggleShare = (shareId: string) => {
    setExpandedShares(prev => {
      const next = new Set(prev);
      if (next.has(shareId)) next.delete(shareId); else next.add(shareId);
      return next;
    });
  };

  const isAdmin = members.find(m => m.user_id === user?.id)?.role === 'admin';

  const handleDeleteCategory = async (catId: string) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    const toolsInCat = groupTools.filter(t => t.category === cat.name);
    if (toolsInCat.length > 0) {
      setDeleteCatId(catId);
      setDeleteCatToolCount(toolsInCat.length);
      setMoveToCatId('');
    } else {
      await deleteCategory(catId);
    }
  };

  const handleConfirmDeleteCat = async () => {
    if (!deleteCatId) return;
    const cat = categories.find(c => c.id === deleteCatId);
    const moveTo = categories.find(c => c.id === moveToCatId);
    if (moveTo && cat) await updateToolsCategory(cat.name, moveTo.name, moveTo.id);
    await deleteCategory(deleteCatId);
    setDeleteCatId(null); setMoveToCatId('');
  };

  const handleSaveEditCat = async () => {
    if (!editCatId || !editCatName.trim()) return;
    setSavingCat(true);
    await updateCategory(editCatId, editCatName.trim(), editCatEmoji);
    setEditCatId(null); setSavingCat(false);
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim() || !currentGroupId) return;
    setAddingCat(true);
    await addCategory(newCatName.trim(), newCatEmoji, currentGroupId, user!.id);
    setNewCatName(''); setNewCatEmoji(''); setShowAddCat(false); setAddingCat(false);
  };

  if (loading) return <Spinner />;

  const listeFiltered = aktivListeId ? items.filter(i => i.list_id === aktivListeId) : items;
  const catFiltered = aktivKategori ? listeFiltered.filter(i => (i.personal_tags ?? []).includes(aktivKategori)) : listeFiltered;
  const loanFiltered = loanableFilter === 'all' ? catFiltered : catFiltered.filter(i => loanableFilter === 'loanable' ? i.loanable : !i.loanable);
  const visteListe = search.trim()
    ? loanFiltered.filter(i =>
        i.navn.toLowerCase().includes(search.trim().toLowerCase()) ||
        (i.personal_tags ?? []).some(t => t.toLowerCase().includes(search.trim().toLowerCase())))
    : loanFiltered;
  const hurtigItems = visteListe.filter(i => i.quick_added);
  const andreItems = visteListe.filter(i => !i.quick_added);
  const brukerTagger = [...new Set(listeFiltered.flatMap(i => i.personal_tags ?? []).filter(Boolean))].sort();
  const tagSuggestions = [...new Set([...savedTags, ...brukerTagger])].sort();

  return (
    <div>
      {/* Søk */}
      {searchOpen && (
        <div style={{ position: 'relative', marginBottom: 13 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', display: 'flex' }}><Search size={16} strokeWidth={1.8} /></span>
          <input autoFocus type="search" placeholder="Søk i inventaret..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '11px 36px 11px 34px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, outline: 'none', background: 'var(--surface)', color: 'var(--text)' }} />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'var(--text3)', color: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
              <XIcon size={12} strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}

      {/* Liste-tabs */}
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Lister</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' as any }}>
        <button onClick={() => setAktivListeId(null)}
          style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 30, fontSize: 13, fontWeight: aktivListeId === null ? 600 : 400, border: `1px solid ${aktivListeId === null ? 'var(--green)' : 'var(--border-strong)'}`, background: aktivListeId === null ? 'var(--green)' : 'var(--surface)', color: aktivListeId === null ? '#fff' : 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Alle ting
        </button>
        {lists.filter(l => l.is_favorite).map(l => {
          const active = aktivListeId === l.id;
          return (
            <div key={l.id} onClick={() => setAktivListeId(active ? null : l.id)}
              style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 30, fontSize: 13, fontWeight: active ? 600 : 400, border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`, background: active ? 'var(--green)' : 'var(--surface)', color: active ? '#fff' : 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {l.emoji && <CategoryIcon emoji={l.emoji} size={13} color={active ? '#fff' : 'var(--text2)'} />}
              {l.name}
              {active && (
                <button onClick={e => { e.stopPropagation(); toggleListFavorite(l.id, false); setAktivListeId(null); }} title="Fjern favoritt"
                  style={{ padding: 0, marginLeft: 2, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <Star size={13} strokeWidth={2} fill="#fff" color="#fff" />
                </button>
              )}
            </div>
          );
        })}
        {(() => {
          const favoriteIds = new Set(lists.filter(l => l.is_favorite).map(l => l.id));
          const dropdownLists = lists.filter(l => !favoriteIds.has(l.id));
          if (dropdownLists.length === 0) return null;
          const activeInDropdown = aktivListeId && !favoriteIds.has(aktivListeId) ? lists.find(l => l.id === aktivListeId) : null;
          return (
            <div ref={listBtnRef} style={{ flexShrink: 0 }}>
              <button
                onClick={() => {
                  const rect = listBtnRef.current?.getBoundingClientRect();
                  if (rect) setListDropPos({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 280) });
                  setShowListDropdown(s => !s);
                }}
                style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 30, fontSize: 13, fontWeight: activeInDropdown ? 600 : 400, border: `1px solid ${activeInDropdown ? 'var(--green)' : 'var(--border-strong)'}`, background: activeInDropdown ? 'var(--green)' : 'var(--surface)', color: activeInDropdown ? '#fff' : 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {activeInDropdown
                  ? <>{activeInDropdown.emoji && <CategoryIcon emoji={activeInDropdown.emoji} size={13} color="#fff" />} {activeInDropdown.name}</>
                  : 'Flere lister'}
                <span style={{ fontSize: 10, opacity: 0.7 }}>{showListDropdown ? ' ▴' : ' ▾'}</span>
              </button>
              {showListDropdown && ReactDOM.createPortal(
                <>
                  <div onClick={() => setShowListDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
                  <div style={{ position: 'fixed', top: listDropPos.top, left: listDropPos.left, zIndex: 200, background: 'var(--surface-solid)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 40px rgba(0,0,0,0.7)', minWidth: 280, maxHeight: 320, overflowY: 'auto' }}>
                    {dropdownLists.map((l, i) => {
                      const active = aktivListeId === l.id;
                      return (
                        <div key={l.id} onClick={() => { setAktivListeId(active ? null : l.id); setShowListDropdown(false); }} style={{
                          width: '100%', padding: '11px 14px',
                          borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                          background: active ? 'var(--green-light)' : 'var(--surface)',
                          color: active ? 'var(--green-dark)' : 'var(--text)',
                          fontSize: 14, fontWeight: active ? 600 : 400,
                          textAlign: 'left', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          {l.emoji ? <CategoryIcon emoji={l.emoji} size={15} color={active ? 'var(--green-dark)' : 'var(--text2)'} /> : <span style={{ width: 15 }} />}
                          <span style={{ flex: 1 }}>{l.name}</span>
                          {active && <span style={{ fontSize: 13 }}>✓</span>}
                          <button onClick={e => { e.stopPropagation(); toggleListFavorite(l.id, !l.is_favorite); }} title="Favoritt"
                            style={{ padding: 4, borderRadius: 6, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <Star size={14} strokeWidth={2} fill={l.is_favorite ? 'var(--accent)' : 'none'} color={l.is_favorite ? 'var(--accent)' : 'var(--text3)'} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>,
                document.body
              )}
            </div>
          );
        })()}
      </div>

      {/* Tagg-filter */}
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Tagger</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' as any }}>
        <button onClick={() => setAktivKategori(null)}
          style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 30, fontSize: 13, fontWeight: aktivKategori === null ? 600 : 400, border: `1px solid ${aktivKategori === null ? 'var(--green)' : 'var(--border-strong)'}`, background: aktivKategori === null ? 'var(--green)' : 'var(--surface)', color: aktivKategori === null ? '#fff' : 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Alle
        </button>
        {(profile?.favorite_tags ?? []).filter(t => brukerTagger.includes(t)).map(t => {
          const active = aktivKategori === t;
          return (
            <div key={t} onClick={() => setAktivKategori(active ? null : t)}
              style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 30, fontSize: 13, fontWeight: active ? 600 : 400, border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`, background: active ? 'var(--green)' : 'var(--surface)', color: active ? '#fff' : 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {t}
              {active && (
                <button onClick={e => { e.stopPropagation(); toggleFavoriteTag(t); setAktivKategori(null); }} title="Fjern favoritt"
                  style={{ padding: 0, marginLeft: 2, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <Star size={13} strokeWidth={2} fill="#fff" color="#fff" />
                </button>
              )}
            </div>
          );
        })}
        {(() => {
          const favoriteTagSet = new Set((profile?.favorite_tags ?? []).filter(t => brukerTagger.includes(t)));
          const dropdownTags = brukerTagger.filter(t => !favoriteTagSet.has(t));
          if (dropdownTags.length === 0) return null;
          const activeInDropdown = aktivKategori && !favoriteTagSet.has(aktivKategori) ? aktivKategori : null;
          return (
            <div ref={tagBtnRef} style={{ flexShrink: 0 }}>
              <button
                onClick={() => {
                  const rect = tagBtnRef.current?.getBoundingClientRect();
                  if (rect) setTagDropPos({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 280) });
                  setShowTagDropdown(s => !s);
                }}
                style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 30, fontSize: 13, fontWeight: activeInDropdown ? 600 : 400, border: `1px solid ${activeInDropdown ? 'var(--green)' : 'var(--border-strong)'}`, background: activeInDropdown ? 'var(--green)' : 'var(--surface)', color: activeInDropdown ? '#fff' : 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {activeInDropdown || 'Flere tagger'}
                <span style={{ fontSize: 10, opacity: 0.7 }}>{showTagDropdown ? ' ▴' : ' ▾'}</span>
              </button>
              {showTagDropdown && ReactDOM.createPortal(
                <>
                  <div onClick={() => setShowTagDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
                  <div style={{ position: 'fixed', top: tagDropPos.top, left: tagDropPos.left, zIndex: 200, background: 'var(--surface-solid)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 40px rgba(0,0,0,0.7)', minWidth: 280, maxHeight: 320, overflowY: 'auto' }}>
                    {dropdownTags.map((t, i) => {
                      const active = aktivKategori === t;
                      const isFav = (profile?.favorite_tags ?? []).includes(t);
                      return (
                        <div key={t} onClick={() => { setAktivKategori(active ? null : t); setShowTagDropdown(false); }} style={{
                          width: '100%', padding: '11px 14px',
                          borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                          background: active ? 'var(--green-light)' : 'var(--surface)',
                          color: active ? 'var(--green-dark)' : 'var(--text)',
                          fontSize: 14, fontWeight: active ? 600 : 400,
                          textAlign: 'left', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <span style={{ flex: 1 }}>{t}</span>
                          {active && <span style={{ fontSize: 13 }}>✓</span>}
                          <button onClick={e => { e.stopPropagation(); toggleFavoriteTag(t); }} title="Favoritt"
                            style={{ padding: 4, borderRadius: 6, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <Star size={14} strokeWidth={2} fill={isFav ? 'var(--accent)' : 'none'} color={isFav ? 'var(--accent)' : 'var(--text3)'} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>,
                document.body
              )}
            </div>
          );
        })()}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {([
          { key: 'all' as const,      label: 'Alle ting',     count: catFiltered.length },
          { key: 'loanable' as const, label: 'Kan lånes ut',   count: catFiltered.filter(i => i.loanable).length },
          { key: 'not' as const,      label: 'Ikke utlånbar',  count: catFiltered.filter(i => !i.loanable).length },
        ]).map(t => {
          const active = loanableFilter === t.key;
          return (
            <button key={t.key} onClick={() => setLoanableFilter(t.key)}
              style={{
                flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                padding: '8px 6px', borderRadius: 20, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`,
                background: active ? 'var(--green)' : 'var(--surface)',
                color: active ? '#fff' : 'var(--text2)',
              }}>
              {t.label} ({t.count})
            </button>
          );
        })}
      </div>

      {aktivListeId && (
        <button onClick={() => openQuickAdd(aktivListeId)}
          style={{ width: '100%', padding: '10px 14px', marginBottom: 14, borderRadius: 'var(--radius-sm)', border: '1px dashed var(--border-strong)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Plus size={14} strokeWidth={2} /> Rask registrering av flere ting
        </button>
      )}

      {visteListe.length === 0 && items.length > 0 && (
        <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text3)' }}>
          <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><Search size={26} color="var(--text3)" strokeWidth={1.8} /></div>
          <div style={{ fontSize: 14 }}>Ingen ting matcher filteret</div>
        </div>
      )}

      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text2)' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><Archive size={32} color="var(--text3)" strokeWidth={1.8} /></div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Ingen ting enda</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>Trykk + øverst for å legge til</div>
        </div>
      )}

      {hurtigItems.length > 0 && (
        <ExpandSection
          key={search.trim() && hurtigItems.length > 0 ? 'search-open' : 'default'}
          label={`Hurtigregistrert (${hurtigItems.length})`}
          defaultOpen={!!search.trim() && hurtigItems.length > 0}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {hurtigItems.map(item => {
              const listNavn = !aktivListeId ? lists.find(l => l.id === item.list_id)?.name : null;
              return (
                <div key={item.id} onClick={() => setDetailId(item.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', cursor: 'pointer' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{highlightMatch(item.navn, search)}</div>
                    {listNavn && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{listNavn}</div>}
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>{item.antall} {item.enhet}</span>
                </div>
              );
            })}
          </div>
        </ExpandSection>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {andreItems.map(item => {
          let statusColor: string | undefined;
          let statusBg: string | undefined;
          let statusLabel: string | undefined;
          let StatusIcon: typeof Check | undefined;
          if (item.loanable) {
            const dueDateLabel = item.due_date ? new Date(item.due_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }) : null;
            if (item.status === 'available') { statusColor = 'var(--green)'; statusBg = 'var(--green-light)'; StatusIcon = Check; statusLabel = 'Ledig'; }
            else if (item.status === 'pending') { statusColor = 'var(--amber)'; statusBg = 'var(--amber-light)'; StatusIcon = Clock; statusLabel = 'Venter'; }
            else { statusColor = 'var(--amber)'; statusBg = 'var(--amber-light)'; StatusIcon = Package; statusLabel = dueDateLabel ? `Utlånt til ${dueDateLabel}` : 'Utlånt'; }
          }
          return (
            <ImageTile
              key={item.id}
              imageUrl={item.bilde_url}
              emoji={item.emoji}
              name={highlightMatch(item.navn, search)}
              onClick={() => setDetailId(item.id)}
              onExpand={item.bilde_url ? () => setLightboxUrl(item.bilde_url) : undefined}
              statusColor={statusColor}
              statusBg={statusBg}
              statusLabel={statusLabel}
              StatusIcon={StatusIcon}
              topLeft={<ImageTileBadge>{item.antall} {item.enhet}</ImageTileBadge>}
            />
          );
        })}
      </div>

      {/* Detaljbunnark */}
      {detailId && (() => {
        const item = items.find(i => i.id === detailId);
        if (!item) return null;
        const tags = item.personal_tags ?? [];
        const loaner = item.loaned_to;
        return (
          <BottomSheet title={item.navn} onClose={() => setDetailId(null)}>
            {item.bilde_url
              ? <div style={{ position: 'relative', marginBottom: 16 }} onClick={() => setLightboxUrl(item.bilde_url!)}>
                  <img src={item.bilde_url} alt={item.navn}
                    style={{ width: '100%', height: 190, objectFit: 'cover', borderRadius: 'var(--radius)', display: 'block', cursor: 'zoom-in' }} />
                  <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.45)', borderRadius: 6, padding: '4px 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Maximize2 size={14} color="#fff" strokeWidth={2} />
                  </div>
                </div>
              : item.emoji && (
                  <div style={{ width: '100%', height: 110, borderRadius: 'var(--radius)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <CategoryIcon emoji={item.emoji} size={52} color="var(--green)" />
                  </div>
                )
            }

            {item.beskrivelse && <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>{item.beskrivelse}</p>}

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              <Badge variant="blue">{item.antall} {item.enhet}</Badge>
              {tags.map(t => <Badge key={t} variant="green">{t}</Badge>)}
              {item.loanable && item.status === 'available' && <Badge variant="green">Kan lånes ut</Badge>}
              {item.loanable && item.status === 'pending' && <Badge variant="amber">Forespørsel mottatt</Badge>}
              {item.loanable && item.status === 'loaned' && loaner && <Badge variant="amber">Utlånt til {loaner.full_name}</Badge>}
            </div>

            {(item.filterCategories ?? []).length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {(item.filterCategories ?? []).map(c => (
                  <span key={c.id} style={{ padding: '3px 10px', borderRadius: 20, background: 'var(--surface)', border: '1px solid var(--border-strong)', fontSize: 12, color: 'var(--text2)' }}>{c.name}</span>
                ))}
              </div>
            )}

            {lists.find(l => l.id === item.list_id) && (
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>
                Liste: <strong style={{ color: 'var(--text2)' }}>{lists.find(l => l.id === item.list_id)?.name}</strong>
              </div>
            )}

            {itemCodes.find(c => c.inventoryId === item.id) && (
              <button onClick={() => setQrTarget({ code: itemCodes.find(c => c.inventoryId === item.id)!.code, label: item.navn })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'var(--green-dark)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 14 }}>
                <QrCode size={14} strokeWidth={2} /> Vis QR-kode
              </button>
            )}

            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 16px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => { setDetailId(null); setEditId(item.id); }}
                style={{ width: '100%', padding: 13, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Pencil size={15} strokeWidth={2} /> Rediger
              </button>
              <button onClick={async () => { setDetailId(null); await handleDelete(item.id); }}
                style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Trash2 size={14} strokeWidth={2} /> Slett
              </button>
            </div>
          </BottomSheet>
        );
      })()}

      {/* Delt med meg */}
      {sharedInventories.length > 0 && (
        <>
          <Divider />
          <SectionLabel>Delt med meg ({sharedInventories.length})</SectionLabel>
          {sharedInventories.map(shared => {
            const expanded = expandedShares.has(shared.shareId);
            return (
              <div key={shared.shareId} style={{ marginBottom: 10 }}>
                <div onClick={() => toggleShare(shared.shareId)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: expanded ? 'var(--radius) var(--radius) 0 0' : 'var(--radius)', cursor: 'pointer' }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {shared.listEmoji && <CategoryIcon emoji={shared.listEmoji} size={14} color="var(--text2)" />}
                      {shared.listName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>av {shared.ownerName} · {shared.items.length} ting</div>
                  </div>
                  <span style={{ color: 'var(--text3)', fontSize: 18, display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</span>
                </div>

                {expanded && (
                  <div style={{ border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 var(--radius) var(--radius)', overflow: 'hidden' }}>
                    {shared.items.length === 0 && (
                      <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Ingen ting enda</div>
                    )}
                    {shared.items.map((item, idx) => (
                      <div key={item.id} style={{ display: 'flex', gap: 10, padding: '10px 14px', borderTop: idx > 0 ? '1px solid var(--border)' : 'none', background: 'var(--bg)' }}>
                        {item.bilde_url
                          ? <img src={item.bilde_url} alt={item.navn} style={{ width: 38, height: 38, borderRadius: 'var(--radius-sm)', objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><CategoryIcon emoji={item.emoji ?? ''} size={18} color="var(--green)" /></div>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{item.navn}</div>
                          {item.beskrivelse ? <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 3 }}>{item.beskrivelse}</div> : null}
                          <div style={{ display: 'flex', gap: 5 }}>
                            <Badge variant="blue">{item.antall} {item.enhet}</Badge>
                            {(item.personal_tags ?? []).map(t => <Badge key={t} variant="green">{t}</Badge>)}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
                      <button onClick={() => handleLeave(shared.shareId, shared.listName, shared.ownerName)}
                        style={{ fontSize: 12, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        Meld meg av denne listen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Legg til */}
      {addOpen && (
        <BottomSheet title="Legg til ting" onClose={onCloseAdd}>
          {error && <ErrorMsg msg={error} />}

          {/* ── Bildekort (hero) ── */}
          {!imagePreview ? (
            <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: '22px 16px 18px', textAlign: 'center', marginBottom: 16, background: 'var(--surface)' }}>
              <Camera size={30} strokeWidth={1.4} style={{ color: 'var(--text3)', marginBottom: 8 }} />
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14, lineHeight: 1.5 }}>
                Legg til et bilde av tingen
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setCameraOpen(true)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  <Camera size={15} strokeWidth={2} /> Ta bilde
                </button>
                <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  <Images size={15} strokeWidth={2} /> Velg bilde
                  <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                </label>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <div style={{ position: 'relative' }}>
                <img src={imagePreview} alt="Forhåndsvisning" style={{ width: '100%', borderRadius: 'var(--radius-sm)', objectFit: 'cover', maxHeight: 180, display: 'block' }} />
                <label style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, padding: '5px 11px', borderRadius: 20, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Images size={12} strokeWidth={2} /> Bytt bilde
                  <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                </label>
              </div>
            </div>
          )}

          <Field label={<>Navn <span style={{ color: 'var(--red)', fontWeight: 700 }}>*</span></>}>
            <Input placeholder="F.eks. Skruer M6" value={form.navn} onChange={e => setForm(f => ({ ...f, navn: e.target.value }))} />
          </Field>

          <Field label="Beskrivelse (valgfritt)">
            <Textarea placeholder="Kort beskrivelse..." value={form.beskrivelse} onChange={e => setForm(f => ({ ...f, beskrivelse: e.target.value }))} rows={2} />
          </Field>

          <Field label="Tagger">
            <TagInput
              value={formTags}
              onChange={setFormTags}
              suggestions={tagSuggestions}
              placeholder="F.eks. Garasje, Boden, Verktøy..."
            />
          </Field>

          <ExpandSection label="Antall og enhet" defaultOpen={false}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text2)', marginBottom: 5 }}>Antall</label>
                <QuantityStepper value={form.antall} onChange={val => setForm(f => ({ ...f, antall: val }))} />
              </div>
              <div style={{ width: 110 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text2)', marginBottom: 5 }}>Enhet</label>
                <UnitPicker
                  value={customEnhet ? form.enhet : form.enhet}
                  options={ENHETER_FORSLAG}
                  onChange={val => {
                    if (val === '__custom__') { setCustomEnhet(true); setForm(f => ({ ...f, enhet: '' })); }
                    else { setCustomEnhet(false); setForm(f => ({ ...f, enhet: val })); }
                  }}
                />
              </div>
            </div>
            {customEnhet && (
              <Input placeholder="Skriv enhet (f.eks. palle, bunt, løpemeter...)" value={form.enhet} onChange={e => setForm(f => ({ ...f, enhet: e.target.value }))} style={{ marginTop: 10 }} autoFocus />
            )}
          </ExpandSection>

          {myAdminOrgIds.size > 0 && (
            <Field label="Hvem eier dette?">
              <Select value={formOrganizationId} onChange={async e => {
                const orgId = e.target.value;
                setFormOrganizationId(orgId);
                if (orgId) {
                  setFormLoanable(true);
                  await fetchOrgGroups(orgId);
                  const orgGroupIds = (useOrganizations.getState().orgGroups[orgId] || []).map(g => g.id);
                  setFormGroupIds(orgGroupIds);
                } else {
                  setFormLoanable(false);
                  setFormGroupIds([]);
                  setFormFilterCategoryIds([]);
                }
              }}>
                <option value="">Deg</option>
                {organizations.filter(o => myAdminOrgIds.has(o.id)).map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </Select>
            </Field>
          )}

          {(() => { const pickableGroups = formOrganizationId ? (orgGroups[formOrganizationId] || []) : groups;
            // Seksjoner pillene etter organisasjon når du eier tingen selv («Deg») —
            // du kan være med i grupper fra flere ulike organisasjoner samtidig.
            const groupSections = formOrganizationId ? null : (() => {
              const personal = pickableGroups.filter(g => !groupOrgInfo[g.id]);
              const byOrg = new Map<string, { name: string; items: typeof pickableGroups }>();
              pickableGroups.forEach(g => {
                const org = groupOrgInfo[g.id];
                if (!org) return;
                if (!byOrg.has(org.id)) byOrg.set(org.id, { name: org.name, items: [] });
                byOrg.get(org.id)!.items.push(g);
              });
              return [{ label: 'Dine grupper', items: personal }, ...Array.from(byOrg.values()).map(v => ({ label: v.name, items: v.items }))].filter(s => s.items.length > 0);
            })();
            return (
          <ExpandSection key={formOrganizationId ? 'utlan-org' : 'utlan-default'} label="Utlån" defaultOpen={!!formOrganizationId}>
            <button type="button" onClick={() => { const next = !formLoanable; setFormLoanable(next); if (!next) { setFormGroupIds([]); setFormFilterCategoryIds([]); } else if (formGroupIds.length === 0) { setFormGroupIds(pickableGroups.map(g => g.id)); } }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 14 }}>
              <div style={{ width: 44, height: 24, borderRadius: 12, background: formLoanable ? 'var(--green)' : 'var(--border-strong)', transition: 'background 0.2s', position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: formLoanable ? 22 : 2, transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 14, color: 'var(--text2)' }}>{formLoanable ? 'Ja – vises i Oversikt' : 'Nei – kun synlig for deg'}</span>
            </button>
            {formLoanable && (
              <>
                {categories.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filterkategori i Oversikt</div>
                      {isAdmin && (
                        <button type="button" onClick={() => setShowCategories(true)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 30, border: '1px solid var(--border)', background: 'none', color: 'var(--text3)', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
                          <Pencil size={10} strokeWidth={2} />
                          Rediger
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {categories.map(c => {
                        const active = formFilterCategoryIds.includes(c.id);
                        return (
                          <button key={c.id} type="button"
                            onClick={() => setFormFilterCategoryIds(ids => active ? ids.filter(id => id !== c.id) : [...ids, c.id])}
                            style={{ padding: '5px 13px', borderRadius: 30, fontSize: 13, border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`, background: active ? 'var(--green)' : 'var(--bg)', color: active ? '#fff' : 'var(--text2)', cursor: 'pointer', fontWeight: active ? 600 : 400, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            {c.emoji && <CategoryIcon emoji={c.emoji} size={13} color={active ? '#fff' : 'var(--text2)'} />}
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: formGroupIds.length === 0 ? 'var(--red)' : 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                    Vis i grupper <span style={{ color: 'var(--red)' }}>*</span>
                  </div>
                  {pickableGroups.length > 0 ? (
                    <>
                      {groupSections ? (
                        groupSections.map(section => (
                          <div key={section.label} style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>{section.label}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {section.items.map(g => (
                                <button key={g.id} type="button"
                                  onClick={() => setFormGroupIds(ids => ids.includes(g.id) ? ids.filter(id => id !== g.id) : [...ids, g.id])}
                                  style={{ padding: '5px 13px', borderRadius: 30, fontSize: 13, fontWeight: formGroupIds.includes(g.id) ? 600 : 400, border: `1px solid ${formGroupIds.includes(g.id) ? 'var(--green)' : 'var(--border-strong)'}`, background: formGroupIds.includes(g.id) ? 'var(--green)' : 'var(--bg)', color: formGroupIds.includes(g.id) ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
                                  {g.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {pickableGroups.map(g => (
                            <button key={g.id} type="button"
                              onClick={() => setFormGroupIds(ids => ids.includes(g.id) ? ids.filter(id => id !== g.id) : [...ids, g.id])}
                              style={{ padding: '5px 13px', borderRadius: 30, fontSize: 13, fontWeight: formGroupIds.includes(g.id) ? 600 : 400, border: `1px solid ${formGroupIds.includes(g.id) ? 'var(--green)' : 'var(--border-strong)'}`, background: formGroupIds.includes(g.id) ? 'var(--green)' : 'var(--bg)', color: formGroupIds.includes(g.id) ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
                              {g.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {formGroupIds.length === 0 && (
                        <div style={{ marginTop: 7, fontSize: 12, color: 'var(--red)' }}>Velg minst én gruppe for å dele tingen</div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                      {formOrganizationId ? 'Organisasjonen har ingen tilkoblede grupper enda.' : 'Du er ikke med i noen grupper enda.'}
                    </div>
                  )}
                </div>
              </>
            )}
          </ExpandSection>
          ); })()}

          <ExpandSection label="Liste og ikon" defaultOpen={false}>
            <Field label="Ikon">
              <CategoryIconPicker value={form.emoji} onChange={v => setForm(f => ({ ...f, emoji: v }))} />
            </Field>
            {lists.length > 0 && (
              <Field label="Liste (valgfritt)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <button type="button" onClick={() => setFormListId(null)}
                    style={{ padding: '5px 13px', borderRadius: 30, fontSize: 13, fontWeight: formListId === null ? 600 : 400, border: `1px solid ${formListId === null ? 'var(--green)' : 'var(--border-strong)'}`, background: formListId === null ? 'var(--green)' : 'var(--bg)', color: formListId === null ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
                    Ingen liste
                  </button>
                  {lists.map(l => (
                    <button key={l.id} type="button" onClick={() => setFormListId(l.id)}
                      style={{ padding: '5px 13px', borderRadius: 30, fontSize: 13, fontWeight: formListId === l.id ? 600 : 400, border: `1px solid ${formListId === l.id ? 'var(--green)' : 'var(--border-strong)'}`, background: formListId === l.id ? 'var(--green)' : 'var(--bg)', color: formListId === l.id ? '#fff' : 'var(--text2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {l.emoji && <CategoryIcon emoji={l.emoji} size={13} color={formListId === l.id ? 'var(--green)' : 'var(--text2)'} />}
                      {l.name}
                    </button>
                  ))}
                </div>
              </Field>
            )}
          </ExpandSection>

          <PrimaryBtn onClick={handleAdd} disabled={saving}>{saving ? 'Lagrer...' : 'Legg til'}</PrimaryBtn>
          <SecondaryBtn onClick={onCloseAdd}>Avbryt</SecondaryBtn>

          {cameraOpen && (
            <CameraCapture
              onCapture={file => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); setCameraOpen(false); }}
              onClose={() => setCameraOpen(false)}
            />
          )}
        </BottomSheet>
      )}

      {/* Rediger */}
      {editId && items.find(i => i.id === editId) && (
        <EditItemSheet
          item={items.find(i => i.id === editId)!}
          onClose={() => setEditId(null)}
          onManageCategories={isAdmin ? () => setShowCategories(true) : undefined}
        />
      )}

      {/* Administrer tagger */}
      {tagAdminOpen && (() => {
        const allTagger = [...new Set([
          ...savedTags,
          ...items.flatMap(i => i.personal_tags ?? []),
        ])].sort();
        const tagCounts = items.reduce((acc, i) => {
          (i.personal_tags ?? []).forEach(t => { acc[t] = (acc[t] || 0) + 1; });
          return acc;
        }, {} as Record<string, number>);

        const handleRename = async (oldTag: string) => {
          const newName = editingTagName.trim();
          if (!newName || newName === oldTag) { setEditingTag(null); return; }
          setTagAdminSaving(true);
          await renamePersonalTag(user!.id, oldTag, newName);
          if (aktivKategori === oldTag) setAktivKategori(newName);
          const favTags = profile?.favorite_tags ?? [];
          if (favTags.includes(oldTag)) {
            await updateProfile({ favorite_tags: favTags.map(t => t === oldTag ? newName : t) });
          }
          setEditingTag(null);
          setTagAdminSaving(false);
        };

        const handleDelete = async (tag: string) => {
          setTagAdminSaving(true);
          await deletePersonalTag(user!.id, tag, replacementTag || undefined);
          if (aktivKategori === tag) setAktivKategori(null);
          const favTags = profile?.favorite_tags ?? [];
          if (favTags.includes(tag)) {
            await updateProfile({ favorite_tags: favTags.filter(t => t !== tag) });
          }
          setDeletingTag(null);
          setReplacementTag('');
          setTagAdminSaving(false);
        };

        return (
          <BottomSheet title="Administrer tagger" onClose={() => { setTagAdminOpen(false); setEditingTag(null); setDeletingTag(null); onCloseTagAdmin?.(); }}>
            {allTagger.length === 0 && (
              <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '16px 0', fontSize: 13 }}>Ingen tagger enda</p>
            )}
            {allTagger.map(tag => (
              <div key={tag} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, marginBottom: 12 }}>
                {editingTag === tag ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Input value={editingTagName} onChange={e => setEditingTagName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(tag); if (e.key === 'Escape') setEditingTag(null); }}
                      autoFocus style={{ flex: 1 }} />
                    <button onClick={() => handleRename(tag)} disabled={tagAdminSaving}
                      style={{ padding: '8px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--green)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      Lagre
                    </button>
                    <button onClick={() => setEditingTag(null)}
                      style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'none', border: '1px solid var(--border-strong)', cursor: 'pointer', fontSize: 13, color: 'var(--text2)' }}>
                      Avbryt
                    </button>
                  </div>
                ) : deletingTag === tag ? (
                  <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '12px', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>
                      <strong>{tagCounts[tag] || 0} item{tagCounts[tag] !== 1 ? 's' : ''}</strong> bruker taggen «{tag}».
                      Velg en erstatningstag, eller slett uten å erstatte.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                      {allTagger.filter(t => t !== tag).map(t => (
                        <button key={t} type="button" onClick={() => setReplacementTag(replacementTag === t ? '' : t)}
                          style={{ padding: '5px 13px', borderRadius: 30, fontSize: 13, cursor: 'pointer',
                            border: `1px solid ${replacementTag === t ? 'var(--green)' : 'var(--border-strong)'}`,
                            background: replacementTag === t ? 'var(--green)' : 'var(--bg)',
                            color: replacementTag === t ? '#fff' : 'var(--text2)' }}>
                          {t}
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleDelete(tag)} disabled={tagAdminSaving}
                        style={{ flex: 1, padding: '9px', borderRadius: 'var(--radius-sm)', background: 'var(--red)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        {tagAdminSaving ? 'Sletter...' : replacementTag ? `Erstatt med «${replacementTag}» og slett` : 'Slett uten å erstatte'}
                      </button>
                      <button onClick={() => { setDeletingTag(null); setReplacementTag(''); }}
                        style={{ padding: '9px 14px', borderRadius: 'var(--radius-sm)', background: 'none', border: '1px solid var(--border-strong)', cursor: 'pointer', fontSize: 13, color: 'var(--text2)' }}>
                        Avbryt
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{tag}</span>
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>{tagCounts[tag] || 0} item{tagCounts[tag] !== 1 ? 's' : ''}</span>
                    <button onClick={() => toggleFavoriteTag(tag)} title="Favoritt"
                      style={{ padding: '5px 8px', borderRadius: 'var(--radius-sm)', background: 'none', border: '1px solid var(--border-strong)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      <Star size={13} strokeWidth={2} fill={(profile?.favorite_tags ?? []).includes(tag) ? 'var(--accent)' : 'none'} color={(profile?.favorite_tags ?? []).includes(tag) ? 'var(--accent)' : 'var(--text2)'} />
                    </button>
                    <button onClick={() => { setEditingTag(tag); setEditingTagName(tag); setDeletingTag(null); }}
                      style={{ padding: '5px 8px', borderRadius: 'var(--radius-sm)', background: 'none', border: '1px solid var(--border-strong)', cursor: 'pointer', color: 'var(--text2)', display: 'flex', alignItems: 'center' }}>
                      <Pencil size={13} strokeWidth={2} />
                    </button>
                    <button onClick={() => { setDeletingTag(tag); setEditingTag(null); setReplacementTag(''); }}
                      style={{ padding: '5px 8px', borderRadius: 'var(--radius-sm)', background: 'none', border: '1px solid #fca5a5', cursor: 'pointer', color: 'var(--red)', display: 'flex', alignItems: 'center' }}>
                      <Trash2 size={13} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </BottomSheet>
        );
      })()}

      {/* Del */}
      {shareOpen && (
        <BottomSheet title="Del inventar" onClose={onCloseShare}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            Del enkeltlister med andre – de kan se, men ikke redigere.
          </p>

          {lists.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              Du har ingen lister enda. Opprett en liste for å kunne dele.
            </div>
          ) : (
            lists.map(list => {
              const listSharedWith = sharedWith.filter(sw => sw.listId === list.id);
              const isCopied = copiedListId === list.id;
              const isGenerating = generatingTokenListId === list.id;
              const emailOpen = activeEmailListId === list.id;
              return (
                <div key={list.id} style={{ marginBottom: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                  {/* Liste-header */}
                  <div style={{ padding: '10px 14px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {list.emoji && <CategoryIcon emoji={list.emoji} size={15} color="var(--text2)" />}
                    <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{list.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>{items.filter(i => i.list_id === list.id).length} ting</span>
                  </div>

                  <div style={{ padding: '12px 14px' }}>
                    {/* Token eller generer-knapp */}
                    {list.share_token ? (
                      <>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                          <div style={{ flex: 1, padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 3, textAlign: 'center' }}>
                            {list.share_token}
                          </div>
                          <button onClick={() => handleCopyToken(list.share_token!, list.id)}
                            style={{ padding: '9px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: isCopied ? 'var(--green-light)' : 'var(--bg)', color: isCopied ? 'var(--green-dark)' : 'var(--text)', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{isCopied ? <><Check size={12} strokeWidth={2.5} /> Kopiert</> : <><Copy size={12} strokeWidth={2} /> Kopier</>}</span>
                          </button>
                        </div>

                        {/* E-post toggle */}
                        <button
                          onClick={() => { setActiveEmailListId(emailOpen ? null : list.id); setShareEmail(''); setShareEmailError(''); setShareEmailSent(false); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: emailOpen ? 8 : 0 }}
                        >
                          <Mail size={12} strokeWidth={2} /> {emailOpen ? 'Skjul e-post' : 'Send på e-post'}
                        </button>

                        {emailOpen && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <Input type="email" placeholder="E-postadresse..." value={shareEmail}
                              onChange={e => { setShareEmail(e.target.value); setShareEmailError(''); }}
                              onKeyDown={e => e.key === 'Enter' && handleSendEmail(list.share_token!)}
                              style={{ flex: 1 }} />
                            <button onClick={() => handleSendEmail(list.share_token!)} disabled={shareEmailSending || !shareEmail.trim()}
                              style={{ padding: '11px 12px', background: shareEmailSent ? 'var(--green-light)' : 'var(--green)', color: shareEmailSent ? 'var(--green-dark)' : '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{shareEmailSending ? '...' : shareEmailSent ? <><Check size={12} /> Sendt</> : <><Mail size={12} /> Send</>}</span>
                            </button>
                          </div>
                        )}
                        {shareEmailError && emailOpen && <ErrorMsg msg={shareEmailError} />}
                      </>
                    ) : (
                      <button onClick={() => handleGenerateToken(list.id)} disabled={isGenerating}
                        style={{ width: '100%', padding: '10px 12px', border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'none', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--text2)' }}
                      >
                        {isGenerating ? 'Genererer...' : <><Plus size={13} strokeWidth={2} /> Generer delekode</>}
                      </button>
                    )}

                    {/* Hvem har tilgang */}
                    {listSharedWith.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                          Har tilgang ({listSharedWith.length})
                        </div>
                        {listSharedWith.map(sw => (
                          <div key={sw.shareId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                            <Avatar name={sw.name} color={sw.color} size="sm" />
                            <span style={{ flex: 1, fontSize: 13 }}>{sw.name}</span>
                            <button onClick={() => handleRevoke(sw.shareId, sw.name)}
                              style={{ fontSize: 12, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
                            >
                              Fjern
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}

          <Divider />
          <SectionLabel>Legg til annens inventar</SectionLabel>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <Input
              placeholder="Skriv kode..."
              value={joinToken}
              onChange={e => setJoinToken(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleJoin()}
              style={{ flex: 1, fontFamily: 'monospace', letterSpacing: 2 }}
            />
            <button onClick={handleJoin} disabled={joinLoading}
              style={{ padding: '11px 14px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              {joinLoading ? '...' : 'Legg til'}
            </button>
          </div>
          {joinError && <ErrorMsg msg={joinError} />}

          <SecondaryBtn onClick={onCloseShare}>Lukk</SecondaryBtn>
        </BottomSheet>
      )}
      {/* Ny liste sheet */}
      {newListOpen && (
        <BottomSheet title="Ny liste" onClose={() => { setNewListOpen(false); setNewListName(''); setNewListEmoji(''); setNewListItems([]); onCloseAddList?.(); }}>
          <Field label="Navn på listen">
            <Input placeholder="F.eks. Garasje, Bod, Kjøkken..." value={newListName} onChange={e => setNewListName(e.target.value)} autoFocus />
          </Field>
          <Field label="Ikon (valgfritt)">
            <CategoryIconPicker value={newListEmoji} onChange={setNewListEmoji} />
          </Field>
          {items.length > 0 && (
            <Field label="Legg til ting (valgfritt)">
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {items.map(item => {
                  const sel = newListItems.includes(item.id);
                  return (
                    <div key={item.id} onClick={() => setNewListItems(prev => sel ? prev.filter(id => id !== item.id) : [...prev, item.id])}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, border: `1px solid ${sel ? 'var(--green)' : 'var(--border-strong)'}`, borderRadius: 'var(--radius-sm)', background: sel ? 'var(--green-light)' : 'var(--surface)', cursor: 'pointer' }}>
                      {item.bilde_url
                        ? <img src={item.bilde_url} alt={item.navn} style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', objectFit: 'cover', flexShrink: 0 }} />
                        : <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <CategoryIcon emoji={item.emoji ?? ''} size={18} color="var(--green)" />
                          </div>}
                      <span style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{item.navn}</span>
                      <span style={{ fontSize: 16 }}>{sel ? '✅' : '⬜'}</span>
                    </div>
                  );
                })}
              </div>
            </Field>
          )}
          {newListError && (
            <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--red-light)', color: 'var(--red)', fontSize: 13 }}>
              {newListError}
            </div>
          )}
          <PrimaryBtn onClick={handleCreateList} disabled={newListSaving || !newListName.trim()}>
            {newListSaving ? 'Oppretter...' : `Opprett liste${newListItems.length > 0 ? ` med ${newListItems.length} ting` : ''}`}
          </PrimaryBtn>
          <SecondaryBtn onClick={() => { setNewListOpen(false); setNewListName(''); setNewListEmoji(''); setNewListItems([]); setNewListError(''); onCloseAddList?.(); }}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Administrer lister sheet */}
      {manageListsOpen && (
        <BottomSheet title="Administrer lister" onClose={() => { setManageListsOpen(false); setEditingListId(null); onCloseManageLists?.(); }}>
          <button
            onClick={() => { setManageListsOpen(false); onCloseManageLists?.(); setNewListOpen(true); }}
            style={{ width: '100%', padding: '11px 14px', marginBottom: 16, borderRadius: 'var(--radius-sm)', border: '1px dashed var(--border-strong)', background: 'none', fontSize: 14, fontWeight: 500, color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Plus size={15} strokeWidth={2} /> Ny liste
          </button>
          {lists.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text3)', fontSize: 14 }}>Ingen lister ennå</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lists.map(l => (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)' }}>
                  {editingListId === l.id ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          autoFocus
                          value={editingListName}
                          onChange={e => setEditingListName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Escape') setEditingListId(null); }}
                          style={{ flex: 1, padding: '5px 8px', border: '1px solid var(--green)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
                        />
                        <button
                          onClick={async () => {
                            if (editingListName.trim()) await renameList(l.id, editingListName.trim(), editingListEmoji);
                            const err = await setListCode(user!.id, l.id, editingListCode.trim() || null);
                            if (err) { setEditingListCodeError(err); return; }
                            setEditingListId(null);
                          }}
                          style={{ padding: '5px 12px', borderRadius: 30, border: 'none', background: 'var(--green)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                          Lagre
                        </button>
                        <button onClick={() => setEditingListId(null)}
                          style={{ padding: '5px 8px', borderRadius: 30, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text3)', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                          <XIcon size={13} strokeWidth={2.5} />
                        </button>
                      </div>
                      <CategoryIconPicker value={editingListEmoji} onChange={setEditingListEmoji} />
                      <Input
                        placeholder="Kode (valgfritt), f.eks. 7 eller B12"
                        value={editingListCode}
                        onChange={e => { setEditingListCode(e.target.value.toUpperCase().slice(0, 12)); setEditingListCodeError(''); }}
                        style={{ fontFamily: 'monospace', letterSpacing: 2 }}
                      />
                      {editingListCodeError && <ErrorMsg msg={editingListCodeError} />}
                    </div>
                  ) : (
                    <>
                      {l.emoji && <CategoryIcon emoji={l.emoji} size={16} color="var(--text2)" />}
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{l.name}</span>
                      <span style={{ fontSize: 12, color: 'var(--text3)', marginRight: 4 }}>
                        {items.filter(i => i.list_id === l.id).length} ting
                      </span>
                      <button
                        onClick={() => toggleListFavorite(l.id, !l.is_favorite)}
                        title={l.is_favorite ? 'Fjern som favoritt' : 'Merk som favoritt'}
                        style={{ padding: '5px 8px', borderRadius: 30, border: '1px solid var(--border-strong)', background: 'none', color: l.is_favorite ? 'var(--accent)' : 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Star size={13} strokeWidth={2} fill={l.is_favorite ? 'var(--accent)' : 'none'} />
                      </button>
                      {itemCodes.find(c => c.listId === l.id) && (
                        <button
                          onClick={() => setQrTarget({ code: itemCodes.find(c => c.listId === l.id)!.code, label: l.name })}
                          title="Vis QR-kode"
                          style={{ padding: '5px 8px', borderRadius: 30, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <QrCode size={13} strokeWidth={2} />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingListId(l.id); setEditingListName(l.name); setEditingListEmoji(l.emoji || '');
                          setEditingListCode(itemCodes.find(c => c.listId === l.id)?.code || '');
                          setEditingListCodeError('');
                        }}
                        title="Endre"
                        style={{ padding: '5px 8px', borderRadius: 30, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Pencil size={13} strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => handleDeleteList(l.id, l.name)}
                        title="Slett liste"
                        style={{ padding: '5px 8px', borderRadius: 30, border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Trash2 size={13} strokeWidth={2} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <SecondaryBtn onClick={() => { setManageListsOpen(false); setEditingListId(null); onCloseManageLists?.(); }}>Lukk</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Slå opp kode */}
      {codeSearchOpen && (
        <BottomSheet title="Slå opp kode" onClose={() => { setCodeSearchOpen(false); onCloseCodeSearch?.(); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
            Skriv inn koden som står på boksen eller tingen.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <Input placeholder="F.eks. 7 eller B12" value={codeSearchValue}
              onChange={e => { setCodeSearchValue(e.target.value.toUpperCase()); setCodeSearchError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleCodeSearch()}
              style={{ flex: 1, fontFamily: 'monospace', letterSpacing: 2 }} maxLength={12} autoFocus />
            <button onClick={handleCodeSearch}
              style={{ padding: '11px 16px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Søk
            </button>
            <button onClick={() => setScannerOpen(true)} aria-label="Skann QR-kode" title="Skann QR-kode"
              style={{ padding: '11px 12px', background: 'var(--surface)', color: 'var(--green)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <ScanLine size={18} strokeWidth={2} />
            </button>
          </div>
          {codeSearchError && <ErrorMsg msg={codeSearchError} />}
          <SecondaryBtn onClick={() => { setCodeSearchOpen(false); onCloseCodeSearch?.(); }}>Lukk</SecondaryBtn>
        </BottomSheet>
      )}

      {scannerOpen && <CodeScanner onScan={handleScannedInSheet} onClose={() => setScannerOpen(false)} />}

      {qrTarget && <QRCodeSheet code={qrTarget.code} title={qrTarget.label} onClose={() => setQrTarget(null)} />}

      {/* Rask registrering */}
      {quickAddOpen && !quickAddListId && (
        <BottomSheet title="Rask registrering" onClose={closeQuickAdd}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
            Velg hvilken liste/boks tingene skal legges i.
          </p>
          {lists.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>Du har ingen lister ennå. Lag en liste først.</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {lists.map(l => (
              <button key={l.id} onClick={() => setQuickAddListId(l.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'var(--surface)', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}>
                {l.emoji && <span>{l.emoji}</span>}
                <span>{l.name}</span>
              </button>
            ))}
          </div>
          <SecondaryBtn onClick={closeQuickAdd}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}
      {quickAddOpen && quickAddListId && (
        <BottomSheet title="Rask registrering" onClose={closeQuickAdd}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
            Legg til flere enkle ting rett i «{lists.find(l => l.id === quickAddListId)?.name || 'listen'}» — ingen bilde eller deling nødvendig. Du kan alltid åpne en av dem senere for å legge til mer.
          </p>
          {quickAddRows.map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
              <input
                placeholder="Navn, f.eks. 4x100 mm utendørs skrue"
                value={row.navn}
                onChange={e => updateQuickRow(i, 'navn', e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: '9px 10px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
              />
              <input
                type="number" min={0}
                placeholder="Antall"
                value={row.antall}
                onChange={e => updateQuickRow(i, 'antall', e.target.value)}
                style={{ width: 64, padding: '9px 8px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
              />
              <input
                placeholder="stk"
                value={row.enhet}
                onChange={e => updateQuickRow(i, 'enhet', e.target.value)}
                style={{ width: 56, padding: '9px 8px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
              />
              <button onClick={() => removeQuickRow(i)} disabled={quickAddRows.length === 1}
                style={{ padding: 8, borderRadius: 30, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text3)', cursor: quickAddRows.length === 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0, opacity: quickAddRows.length === 1 ? 0.4 : 1 }}>
                <XIcon size={13} strokeWidth={2.5} />
              </button>
            </div>
          ))}
          <button onClick={addQuickRow}
            style={{ width: '100%', padding: '9px 14px', marginBottom: 14, borderRadius: 'var(--radius-sm)', border: '1px dashed var(--border-strong)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Plus size={14} strokeWidth={2} /> Legg til enda en rad
          </button>
          {quickAddError && <ErrorMsg msg={quickAddError} />}
          <PrimaryBtn onClick={handleQuickAddSave} disabled={quickAddSaving}>{quickAddSaving ? 'Lagrer...' : 'Lagre alle'}</PrimaryBtn>
          <SecondaryBtn onClick={closeQuickAdd}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Slett liste med gjenstander */}
      {deleteListSheet && (
        <BottomSheet title={`Slett «${deleteListSheet.name}»`} onClose={() => setDeleteListSheet(null)}>
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16 }}>
            Listen inneholder <strong>{deleteListSheet.count} gjenstand{deleteListSheet.count > 1 ? 'er' : ''}</strong>. Hva skal skje med dem?
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {/* Alternativ: flytt til annen liste */}
            {lists.filter(l => l.id !== deleteListSheet.id).length > 0 && (
              <div style={{ border: `2px solid ${deleteMoveTarget !== undefined && deleteMoveTarget !== '__delete__' ? 'var(--green)' : 'var(--border-strong)'}`, borderRadius: 'var(--radius-sm)', padding: '12px 14px', cursor: 'pointer', background: deleteMoveTarget !== undefined && deleteMoveTarget !== '__delete__' ? 'var(--green-light)' : 'var(--surface)' }}
                onClick={() => setDeleteMoveTarget(deleteMoveTarget === '__delete__' || deleteMoveTarget === null ? '' : deleteMoveTarget)}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: deleteMoveTarget !== '__delete__' ? 10 : 0 }}>↗ Flytt til en annen liste</div>
                {deleteMoveTarget !== '__delete__' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <button type="button" onClick={e => { e.stopPropagation(); setDeleteMoveTarget(null); }}
                      style={{ padding: '4px 11px', borderRadius: 30, fontSize: 12, border: `1px solid ${deleteMoveTarget === null ? 'var(--green)' : 'var(--border-strong)'}`, background: deleteMoveTarget === null ? 'var(--green-light)' : 'var(--bg)', color: deleteMoveTarget === null ? 'var(--green)' : 'var(--text2)', cursor: 'pointer' }}>
                      Ingen liste
                    </button>
                    {lists.filter(l => l.id !== deleteListSheet.id).map(l => (
                      <button key={l.id} type="button" onClick={e => { e.stopPropagation(); setDeleteMoveTarget(l.id); }}
                        style={{ padding: '4px 11px', borderRadius: 30, fontSize: 12, border: `1px solid ${deleteMoveTarget === l.id ? 'var(--green)' : 'var(--border-strong)'}`, background: deleteMoveTarget === l.id ? 'var(--green-light)' : 'var(--bg)', color: deleteMoveTarget === l.id ? 'var(--green)' : 'var(--text2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {l.emoji && <CategoryIcon emoji={l.emoji} size={12} color={deleteMoveTarget === l.id ? 'var(--green)' : 'var(--text2)'} />}
                        {l.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Alternativ: slett gjenstander */}
            <div style={{ border: `2px solid ${deleteMoveTarget === '__delete__' ? 'var(--red)' : 'var(--border-strong)'}`, borderRadius: 'var(--radius-sm)', padding: '12px 14px', cursor: 'pointer', background: deleteMoveTarget === '__delete__' ? 'var(--red-light)' : 'var(--surface)' }}
              onClick={() => setDeleteMoveTarget('__delete__')}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--red)' }}>🗑 Slett alle {deleteListSheet.count} gjenstander</div>
            </div>
          </div>

          <button
            onClick={() => handleConfirmDeleteList(deleteMoveTarget === '__delete__' ? 'delete' : 'move')}
            disabled={deleteMoveTarget === undefined}
            style={{ width: '100%', padding: '13px 0', borderRadius: 'var(--radius-sm)', border: 'none', background: deleteMoveTarget === '__delete__' ? 'var(--red)' : 'var(--green)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: deleteMoveTarget === undefined ? 'not-allowed' : 'pointer', opacity: deleteMoveTarget === undefined ? 0.5 : 1, marginBottom: 8 }}>
            {deleteMoveTarget === '__delete__' ? 'Slett gjenstander og liste' : 'Flytt og slett liste'}
          </button>
          <SecondaryBtn onClick={() => setDeleteListSheet(null)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Kategorier sheet */}
      {(showCategories || categoryOpen) && (
        <BottomSheet title="Filterkategorier" onClose={() => { setShowCategories(false); onCloseCategory(); setShowAddCat(false); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            Filterkategorier vises som filterpiller i Oversikt-fanen og gjør det lettere for gruppemedlemmer å finne det de leter etter. Tildel kategorier på hvert enkelt item under Utlån i redigeringsskjemaet. Kun gruppens admin kan opprette og administrere kategorier.
          </p>
          {categories.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '16px 0' }}>Ingen kategorier enda</p>}
          {categories.map(c => (
            <div key={c.id}>
              {editCatId === c.id ? (
                <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 8 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Input placeholder="Kategorinavn" value={editCatName} onChange={e => setEditCatName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSaveEditCat()} autoFocus />
                    <CategoryIconPicker value={editCatEmoji} onChange={setEditCatEmoji} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleSaveEditCat} disabled={savingCat} style={{ flex: 1, padding: 9, background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                      {savingCat ? '...' : 'Lagre'}
                    </button>
                    <button onClick={() => setEditCatId(null)} style={{ flex: 1, padding: 9, background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 13, cursor: 'pointer' }}>Avbryt</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ width: 32, height: 32, borderRadius: 'var(--radius-xs)', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><CategoryIcon emoji={c.emoji} size={18} color="var(--text2)" /></span>
                  <span style={{ flex: 1, fontWeight: 500 }}>{c.name}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setEditCatId(c.id); setEditCatName(c.name); setEditCatEmoji(c.emoji); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={15} strokeWidth={2} /></button>
                    <button onClick={() => handleDeleteCategory(c.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={15} strokeWidth={2} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div style={{ marginTop: 16 }}>
            {!showAddCat ? (
              <button onClick={() => setShowAddCat(true)} style={{ width: '100%', padding: 12, background: 'var(--green-light)', color: 'var(--green-dark)', border: '1px dashed var(--green)', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                + Legg til ny kategori
              </button>
            ) : (
              <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Ny kategori</div>
                <div style={{ marginBottom: 10 }}>
                  <Input placeholder="Navn på kategori" value={newCatName} onChange={e => setNewCatName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddCategory()} autoFocus style={{ marginBottom: 8 }} />
                  <CategoryIconPicker value={newCatEmoji} onChange={setNewCatEmoji} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleAddCategory} disabled={addingCat} style={{ flex: 1, padding: 10, background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                    {addingCat ? 'Lagrer...' : 'Legg til'}
                  </button>
                  <button onClick={() => { setShowAddCat(false); setNewCatName(''); setNewCatEmoji(''); }} style={{ flex: 1, padding: 10, background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, cursor: 'pointer' }}>
                    Avbryt
                  </button>
                </div>
              </div>
            )}
          </div>

          {deleteCatId && (
            <div style={{ background: 'var(--amber-light)', borderRadius: 'var(--radius-sm)', padding: 14, marginTop: 16 }}>
              <div style={{ fontWeight: 600, color: 'var(--amber)', marginBottom: 8 }}>
                ⚠️ {deleteCatToolCount} verktøy bruker denne kategorien
              </div>
              <div style={{ fontSize: 13, color: 'var(--amber)', marginBottom: 12 }}>
                Velg hvilken kategori verktøyene skal flyttes til før du sletter:
              </div>
              <select value={moveToCatId} onChange={e => setMoveToCatId(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 10, background: 'var(--surface)' }}>
                <option value="">Velg kategori...</option>
                {categories.filter(c => c.id !== deleteCatId).map(c => (
                  <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleConfirmDeleteCat} disabled={!moveToCatId} style={{ flex: 1, padding: 10, background: moveToCatId ? 'var(--red)' : 'var(--text3)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, cursor: moveToCatId ? 'pointer' : 'not-allowed' }}>
                  Flytt og slett
                </button>
                <button onClick={() => { setDeleteCatId(null); setMoveToCatId(''); }} style={{ flex: 1, padding: 10, background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 13, cursor: 'pointer' }}>
                  Avbryt
                </button>
              </div>
            </div>
          )}

          <SecondaryBtn onClick={() => { setShowCategories(false); onCloseCategory(); setShowAddCat(false); setDeleteCatId(null); }}>Lukk</SecondaryBtn>
        </BottomSheet>
      )}

      {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}

      {dialog}
    </div>
  );
};
